import type { TFunction } from 'i18next';
import type { CSSProperties } from 'react';

import type {
  BaseListOption,
  EnrichedHeaderListChildOption,
  EnrichedListOption,
  HeaderListOption,
  HeaderOptionMap,
} from '../types.ts';
import { HEADER_GUTTER_SIZE, ITEM_SIZE, PADDING_SIZE } from '../virtualList/constants.ts';

export const enrichWithIsOnList = <T extends { id: string; name: string }>(map: Record<string, T>) => {
  const enrichedMap: Record<string, EnrichedListOption<T>> = {};

  for (const property in map) {
    enrichedMap[property] = { ...map[property], isOnList: false };
  }

  return enrichedMap;
};

export const sortOptions = <T extends { name: string }[]>(value: T) =>
  value.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

export function prepareMapByToLowerCase<T>(list: T[], keyName: string): Record<string, T> {
  return list.reduce((listMap, currentOption) => {
    listMap[currentOption[keyName].toString().toLowerCase()] = currentOption;
    return listMap;
  }, {});
}

export const flattenHeaderList = <T extends HeaderListOption<BaseListOption>>(
  data: T[],
  collapsedHeaders: Record<string, boolean>
) => {
  return data.reduce(
    (acc: (EnrichedHeaderListChildOption<BaseListOption> | HeaderListOption<BaseListOption>)[], header) => {
      const nodes = header.nodes;

      acc.push({ ...header, nodeCount: nodes.length });

      if (nodes.length && collapsedHeaders[header.id]) {
        nodes.forEach(node => {
          acc.push(node);
        });
      }

      return acc;
    },
    []
  );
};

export const filterHeaderListBySearch = <T extends HeaderListOption<BaseListOption>>(data: T[], searchInput: string) =>
  data
    // filter not matching nodes
    .map(header => ({
      ...header,
      nodes: header.nodes.filter(node => node.name.toLowerCase().includes(searchInput.toLowerCase())),
    }))
    // filter empty and not matching category
    .filter(header => header.nodes.length || header.name.toLowerCase().includes(searchInput.toLowerCase()));

export const isHeaderItem = (
  item: EnrichedHeaderListChildOption<BaseListOption> | HeaderListOption<BaseListOption>
): item is HeaderListOption<BaseListOption> => {
  return 'nodes' in item;
};

export const isNonEmptyArr = <T>(arr: Array<T>): arr is [T, ...T[]] => arr.length > 0;

export const virtualizedHeaderListItemSize = <
  T extends HeaderListOption<BaseListOption> | EnrichedHeaderListChildOption<BaseListOption>,
>(
  data: T[],
  index: number
): number => {
  const item = data[index];

  if (isHeaderItem(item)) {
    if (item.nodes.length) {
      return ITEM_SIZE;
    }

    return ITEM_SIZE + HEADER_GUTTER_SIZE;
  }

  return ITEM_SIZE;
};

export const prepareCollapseHeaders = <T extends { id: string }>(data: T[]): Record<string, boolean> => {
  return data.reduce((acc, header) => {
    acc[header.id] = true;

    return acc;
  }, {});
};

export function getBulkPlaceholder(t: TFunction) {
  return t(
    `Items may be entered as either comma, semicolon, or new line separated values.

  Examples:
  Example1,Example2
  or
  Example1;Example2
  or
  Example1
  Example2
  `
  );
}

export const convertHeaderMapToFlatMap = <T extends HeaderOptionMap<BaseListOption>>(
  headerMap: Record<string, T>
): Record<string, EnrichedHeaderListChildOption<T>> => {
  const flatMap = {};

  for (const category in headerMap) {
    const nodes = headerMap[category].nodes;

    for (const node in nodes) {
      flatMap[node] = nodes[node];
    }
  }

  return flatMap;
};

/**
 * react-window v2 positions rows with `transform: translateY(...)` (not `top`).
 * Offset rows so list content keeps the previous inner padding behavior.
 */
export const offsetListRowStyle = (
  style: CSSProperties,
  height: number = style.height as number,
  offsetY: number = PADDING_SIZE
): CSSProperties => {
  const match = typeof style.transform === 'string' ? style.transform.match(/translateY\((-?[\d.]+)px\)/) : null;
  const currentY = match ? Number(match[1]) : 0;

  return {
    ...style,
    height,
    transform: `translateY(${currentY + offsetY}px)`,
  };
};

/** Extra height on the last row's slot so top/bottom padding remain scrollable. */
export const getPaddedRowHeight = (baseHeight: number, index: number, rowCount: number): number => {
  if (rowCount === 0) {
    return baseHeight;
  }

  return index === rowCount - 1 ? baseHeight + PADDING_SIZE * 2 : baseHeight;
};
